import React, { createContext, useContext, useState, useEffect } from 'react';
import defaultPermissions from './permissionsConfig.json';

const PermissionsContext = createContext();

const API_URL = 'https://cntatapi.azurewebsites.net/api/pcontrol';
const API_KEY = process.env.REACT_APP_API_KEY;

export const PermissionsProvider = ({ children }) => {
    const [permissions, setPermissions] = useState(defaultPermissions);
    const [nameOverride, setNameOverride] = useState('');
    const [showControlPanel, setShowControlPanel] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch permissions from API
    const fetchPermissions = async () => {
        setIsLoading(true);
        try {
            console.log('=== FETCHING PERMISSIONS ===');
            console.log('API URL:', API_URL);
            console.log('API Key present?', API_KEY ? 'YES' : 'NO');
            
            const response = await fetch(API_URL, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Response status:', response.status);
            console.log('Response statusText:', response.statusText);
            console.log('Response ok:', response.ok);
            console.log('Response headers:', [...response.headers.entries()]);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response body:', errorText);
                throw new Error(`API returned ${response.status}: ${errorText || response.statusText}`);
            }

            const responseText = await response.text();
            console.log('Response body length:', responseText.length);
            console.log('Response body:', responseText);
            
            if (!responseText || responseText.trim() === '' || responseText.trim() === 'null') {
                console.warn('API returned null/empty - document not found in database');
                throw new Error('Document not found in database. Check CONTROL_PANEL_DOC_ID in Azure Function settings.');
            }

            const data = JSON.parse(responseText);
            
            if (!data) {
                throw new Error('Parsed data is null');
            }
            
            console.log('Parsed data structure:', Object.keys(data));
            console.log('Full parsed data:', data);
            
            if (data && data.permissions) {
                console.log('Using data.permissions');
                setPermissions(data.permissions);
            } else if (data && data.links) {
                console.log('Using data directly (has links)');
                setPermissions(data);
            } else {
                console.warn('Unexpected data structure, using defaults');
                setPermissions(defaultPermissions);
            }
            
            setError(null);
        } catch (err) {
            console.error('=== FETCH ERROR ===');
            console.error('Error type:', err.name);
            console.error('Error message:', err.message);
            console.error('Full error:', err);
            setError(err.message);
            setPermissions(defaultPermissions);
        } finally {
            setIsLoading(false);
        }
    };

    // Load permissions from API on mount
    useEffect(() => {
        fetchPermissions();

        const savedOverride = localStorage.getItem('nameOverride');
        if (savedOverride) {
            setNameOverride(savedOverride);
        }

        // Auto-refresh permissions every 30 seconds
        const pollInterval = setInterval(() => {
            fetchPermissions();
        }, 30000); // 30 seconds

        return () => clearInterval(pollInterval);
    }, []);

    // Save permissions to API
    const updatePermissions = async (newPermissions) => {
        setPermissions(newPermissions);
        
        try {
            console.log('=== UPDATING PERMISSIONS ===');
            console.log('New permissions:', newPermissions);
            console.log('Request body:', JSON.stringify({ permissions: newPermissions }));
            
            const response = await fetch(API_URL, {
                method: 'PUT',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ permissions: newPermissions })
            });

            console.log('PUT Response status:', response.status);
            console.log('PUT Response ok:', response.ok);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('PUT Error response:', errorText);
                throw new Error(`Failed to update permissions: ${response.status} - ${errorText}`);
            }

            const responseText = await response.text();
            console.log('PUT Response body:', responseText);

            setError(null);
        } catch (err) {
            console.error('=== UPDATE ERROR ===');
            console.error('Error updating permissions:', err);
            setError(err.message);
            // Revert to previous state on error
            await fetchPermissions();
        }
    };

    // Update name override
    const updateNameOverride = (name) => {
        setNameOverride(name);
        localStorage.setItem('nameOverride', name);
    };

    // Clear name override
    const clearNameOverride = () => {
        setNameOverride('');
        localStorage.removeItem('nameOverride');
    };

    // Check if user has access to the control panel
    const hasControlPanelAccess = (userName) => {
        const effectiveName = nameOverride || userName;
        
        // If no users specified, everyone has access
        if (!permissions.controlPanelAccess || permissions.controlPanelAccess.length === 0) {
            return true;
        }
        
        return permissions.controlPanelAccess.includes(effectiveName);
    };

    // Check if a user has access to a specific link
    const hasAccess = (linkId, userName) => {
        const effectiveName = nameOverride || userName;
        const link = permissions.links.find(l => l.id === linkId);
        
        if (!link) return false;
        
        // If no users specified, everyone has access
        if (!link.allowedUsers || link.allowedUsers.length === 0) {
            return true;
        }
        
        // Check if the user is in the allowed list
        return link.allowedUsers.includes(effectiveName);
    };

    // Get all links that a user can access
    const getAccessibleLinks = (userName) => {
        const effectiveName = nameOverride || userName;
        return permissions.links.filter(link => {
            if (!link.allowedUsers || link.allowedUsers.length === 0) {
                return true;
            }
            return link.allowedUsers.includes(effectiveName);
        });
    };

    // Add a user to a link's allowed list
    const addUserToLink = (linkId, userName) => {
        const newPermissions = { ...permissions };
        const link = newPermissions.links.find(l => l.id === linkId);
        
        if (link) {
            if (!link.allowedUsers) {
                link.allowedUsers = [];
            }
            if (!link.allowedUsers.includes(userName)) {
                link.allowedUsers.push(userName);
                updatePermissions(newPermissions);
            }
        }
    };

    // Remove a user from a link's allowed list
    const removeUserFromLink = (linkId, userName) => {
        const newPermissions = { ...permissions };
        const link = newPermissions.links.find(l => l.id === linkId);
        
        if (link && link.allowedUsers) {
            link.allowedUsers = link.allowedUsers.filter(u => u !== userName);
            updatePermissions(newPermissions);
        }
    };

    // Add a new link
    const addLink = (linkData) => {
        const newPermissions = { ...permissions };
        if (!newPermissions.links) {
            newPermissions.links = [];
        }
        // Generate a unique ID if not provided
        const linkId = linkData.id || linkData.name.toLowerCase().replace(/\s+/g, '-');
        
        // Check if link with this ID already exists
        if (newPermissions.links.find(l => l.id === linkId)) {
            throw new Error('A link with this ID already exists');
        }
        
        newPermissions.links.push({
            id: linkId,
            name: linkData.name,
            url: linkData.url,
            allowedUsers: linkData.allowedUsers || []
        });
        updatePermissions(newPermissions);
    };

    // Remove a link
    const removeLink = (linkId) => {
        const newPermissions = { ...permissions };
        if (newPermissions.links) {
            newPermissions.links = newPermissions.links.filter(l => l.id !== linkId);
            updatePermissions(newPermissions);
        }
    };

    // Add a user to control panel access
    const addUserToControlPanel = (userName) => {
        const newPermissions = { ...permissions };
        if (!newPermissions.controlPanelAccess) {
            newPermissions.controlPanelAccess = [];
        }
        if (!newPermissions.controlPanelAccess.includes(userName)) {
            newPermissions.controlPanelAccess.push(userName);
            updatePermissions(newPermissions);
        }
    };

    // Remove a user from control panel access
    const removeUserFromControlPanel = (userName) => {
        const newPermissions = { ...permissions };
        if (newPermissions.controlPanelAccess) {
            newPermissions.controlPanelAccess = newPermissions.controlPanelAccess.filter(u => u !== userName);
            updatePermissions(newPermissions);
        }
    };

    // Reset permissions to default
    const resetPermissions = () => {
        updatePermissions(defaultPermissions);
    };

    const value = {
        permissions,
        nameOverride,
        showControlPanel,
        setShowControlPanel,
        updateNameOverride,
        clearNameOverride,
        hasControlPanelAccess,
        hasAccess,
        getAccessibleLinks,
        addUserToLink,
        removeUserFromLink,
        addLink,
        removeLink,
        addUserToControlPanel,
        removeUserFromControlPanel,
        resetPermissions,
        updatePermissions,
        isLoading,
        error,
        refreshPermissions: fetchPermissions
    };

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (!context) {
        throw new Error('usePermissions must be used within a PermissionsProvider');
    }
    return context;
};
